import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Webcam from 'react-webcam';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Stack, Typography } from '@mui/material';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';

import IconButton from '@ui-lib/components/IconButton';
import Button from '@ui-lib/components/Button';
import { createSx } from '@ui-lib/utils.ts';
import { CAMERA_FACING_MODE } from './const.ts';
import CameraSwitcher from './components/CameraSwitcher';
import TorchToggle from './components/TorchToggle.tsx';
import useCapture from './hooks/useCapture.ts';

interface Props {
  onClose: () => void;
  onCapture: (imageSrc: string) => void;
  cameraError: string | null;
  setCameraError: React.Dispatch<React.SetStateAction<string | null>>;
}

const sx = createSx({
  content: {
    position: 'relative',
    overflow: 'hidden',
    height: '100%',
    width: '100%'
  },
  actions: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    p: 2,
    bgcolor: 'background.paper'
  },
  closeBtn: {
    position: 'absolute',
    top: 10,
    right: 10
  }
});

export const CameraPreview = ({ onClose, onCapture, cameraError, setCameraError }: Props) => {
  const { t } = useTranslation();
  const webcamRef = useRef<Webcam>(null);
  const [facingMode, setFacingMode] = useState<CAMERA_FACING_MODE>(CAMERA_FACING_MODE.ENVIRONMENT);
  const capture = useCapture(webcamRef, onCapture, onClose);

  const [isTabletOrMobileView, setIsTabletOrMobileView] = useState(false);

  const handleUserMedia = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoInputs = devices.filter((device) => device.kind === 'videoinput');
      setIsTabletOrMobileView(videoInputs.length > 1);
    } catch {
      setIsTabletOrMobileView(false);
    }
  };

  return (
    <Box sx={sx.content}>
      {cameraError ? (
        <Stack p={2} height='100%' justifyContent='center' alignItems='center' minWidth={250}>
          <ErrorOutlineOutlinedIcon color='error' fontSize='large' />
          <Typography variant='body2' color='secondary'>{cameraError}</Typography>
        </Stack>
      ) : (
        <>
          <Webcam
            audio={false}
            ref={webcamRef}
            screenshotFormat='image/jpeg'
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onUserMediaError={(error) => {
              if (error instanceof DOMException) {
                setCameraError(error.message);
              } else {
                setCameraError(error);
              }
            }}
            screenshotQuality={1}
            videoConstraints={{
              facingMode: facingMode,
              width: { ideal: 4096 },
              height: { ideal: 2160 }
            }}
            onUserMedia={handleUserMedia}
          />
          <Stack sx={sx.actions} spacing={1}>
            <Stack spacing={1} direction='row'>
              {isTabletOrMobileView && <>
                <CameraSwitcher
                  onClick={() => {
                    setFacingMode(
                      facingMode === CAMERA_FACING_MODE.USER
                        ? CAMERA_FACING_MODE.ENVIRONMENT
                        : CAMERA_FACING_MODE.USER
                    );
                  }}
                />
                {facingMode === CAMERA_FACING_MODE.ENVIRONMENT && <TorchToggle webcamRef={webcamRef}/>}
              </>}
            </Stack>
            <Button fullWidth variant='contained' size='large' onClick={capture}>
              {t('bolCreatePage_takePhoto')}
            </Button>
          </Stack>
        </>
      )}
      <Box sx={sx.closeBtn}>
        <IconButton
          variant='contained'
          title={t('close')}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default CameraPreview;
